export interface MetricDefinition {
  key: string;
  name: string;
  description: string;
  formula?: string;
  unit?: string;
  category: 'dora' | 'velocity' | 'quality' | 'efficiency' | 'delivery';
  interpretation?: {
    good?: string;
    warning?: string;
    critical?: string;
  };
  relatedMetrics?: string[];
}

export const METRIC_DEFINITIONS: Record<string, MetricDefinition> = {
  // DORA Metrics
  deploymentFrequency: {
    key: 'deploymentFrequency',
    name: 'Deployment Frequency',
    description: 'How often your team successfully releases to production',
    formula: 'Successful deployments / Time period',
    unit: 'deployments per day/week/month',
    category: 'dora',
    interpretation: {
      good: 'Multiple deployments per day (Elite)',
      warning: 'Between once per week and once per month (Medium)',
      critical: 'Less than once per month (Low)',
    },
    relatedMetrics: ['leadTimeForChanges', 'changeFailureRate'],
  },
  leadTimeForChanges: {
    key: 'leadTimeForChanges',
    name: 'Lead Time for Changes',
    description: 'Time from code commit to production deployment',
    formula: 'Average time from commit to production',
    unit: 'hours/days',
    category: 'dora',
    interpretation: {
      good: 'Less than one hour (Elite)',
      warning: 'Between one week and one month (Medium)',
      critical: 'More than one month (Low)',
    },
    relatedMetrics: ['deploymentFrequency', 'changeFailureRate'],
  },
  changeFailureRate: {
    key: 'changeFailureRate',
    name: 'Change Failure Rate',
    description: 'Percentage of deployments causing production failures',
    formula: '(Failed deployments / Total deployments) × 100',
    unit: 'percentage',
    category: 'dora',
    interpretation: {
      good: 'Less than 15% (Elite)',
      warning: '16-30% (Medium)',
      critical: 'More than 30% (Low)',
    },
    relatedMetrics: ['meanTimeToRecovery', 'deploymentFrequency'],
  },
  meanTimeToRecovery: {
    key: 'meanTimeToRecovery',
    name: 'Mean Time to Recovery',
    description: 'Average time to restore service after production incidents',
    formula: 'Total recovery time / Number of incidents',
    unit: 'hours/days',
    category: 'dora',
    interpretation: {
      good: 'Less than one hour (Elite)',
      warning: 'Between one day and one week (Medium)',
      critical: 'More than one week (Low)',
    },
    relatedMetrics: ['changeFailureRate', 'deploymentFrequency'],
  },

  // Velocity Metrics
  velocity: {
    key: 'velocity',
    name: 'Team Velocity',
    description: 'Story points or work items completed per sprint',
    formula: 'Sum of completed story points / Sprint',
    unit: 'story points per sprint',
    category: 'velocity',
    interpretation: {
      good: 'Consistent and predictable velocity',
      warning: 'High variance in velocity',
      critical: 'Declining velocity trend',
    },
    relatedMetrics: ['sayDoRatio', 'scopeChanges'],
  },
  sayDoRatio: {
    key: 'sayDoRatio',
    name: 'Say/Do Ratio',
    description: 'Percentage of committed work completed within sprint',
    formula: '(Completed story points / Committed story points) × 100',
    unit: 'percentage',
    category: 'velocity',
    interpretation: {
      good: '85-100% (Reliable delivery)',
      warning: '70-84% (Some predictability issues)',
      critical: 'Less than 70% (Poor predictability)',
    },
    relatedMetrics: ['velocity', 'scopeChanges'],
  },
  scopeChanges: {
    key: 'scopeChanges',
    name: 'Scope Changes',
    description: 'Number of work items added/removed during sprint',
    formula: 'Items added + Items removed during sprint',
    unit: 'count',
    category: 'velocity',
    interpretation: {
      good: 'Less than 10% of sprint capacity',
      warning: '10-20% of sprint capacity',
      critical: 'More than 20% of sprint capacity',
    },
    relatedMetrics: ['sayDoRatio', 'velocity'],
  },

  // Quality Metrics
  codeReviewTime: {
    key: 'codeReviewTime',
    name: 'Code Review Time',
    description: 'Average time from PR creation to approval',
    formula: 'Sum of review times / Number of PRs',
    unit: 'hours',
    category: 'quality',
    interpretation: {
      good: 'Less than 24 hours',
      warning: '24-72 hours',
      critical: 'More than 72 hours',
    },
    relatedMetrics: ['pullRequestSize', 'defectRate'],
  },
  pullRequestSize: {
    key: 'pullRequestSize',
    name: 'Pull Request Size',
    description: 'Average lines of code changed per pull request',
    formula: 'Sum of lines changed / Number of PRs',
    unit: 'lines of code',
    category: 'quality',
    interpretation: {
      good: 'Less than 200 lines',
      warning: '200-500 lines',
      critical: 'More than 500 lines',
    },
    relatedMetrics: ['codeReviewTime', 'defectRate'],
  },
  defectRate: {
    key: 'defectRate',
    name: 'Defect Rate',
    description: 'Number of bugs found per feature delivered',
    formula: 'Total bugs / Total features delivered',
    unit: 'bugs per feature',
    category: 'quality',
    interpretation: {
      good: 'Less than 0.1 bugs per feature',
      warning: '0.1-0.3 bugs per feature',
      critical: 'More than 0.3 bugs per feature',
    },
    relatedMetrics: ['changeFailureRate', 'codeReviewTime'],
  },

  // Efficiency Metrics
  roiEfficiency: {
    key: 'roiEfficiency',
    name: 'ROI Efficiency',
    description: 'Return on investment for delivered features',
    formula: '(Business value delivered / Development cost) × 100',
    unit: 'percentage',
    category: 'efficiency',
    interpretation: {
      good: 'ROI > 200%',
      warning: 'ROI 100-200%',
      critical: 'ROI < 100%',
    },
    relatedMetrics: ['featureCost', 'businessValue'],
  },
  featureCost: {
    key: 'featureCost',
    name: 'Average Feature Cost',
    description: 'Average development cost per feature delivered',
    formula: 'Total development cost / Number of features',
    unit: 'currency',
    category: 'efficiency',
    interpretation: {
      good: 'Decreasing cost trend',
      warning: 'Stable cost',
      critical: 'Increasing cost trend',
    },
    relatedMetrics: ['roiEfficiency', 'velocity'],
  },
  effortDistribution: {
    key: 'effortDistribution',
    name: 'Effort Distribution',
    description: 'Percentage breakdown of effort across categories',
    formula: '(Category effort / Total effort) × 100',
    unit: 'percentage',
    category: 'efficiency',
    interpretation: {
      good: '60% new development, 30% maintenance, 10% tech debt',
      warning: '50% new development, 35% maintenance, 15% tech debt',
      critical: 'Less than 40% new development',
    },
    relatedMetrics: ['technicalDebt', 'maintenanceEffort'],
  },

  // Delivery Metrics
  featuresDelivered: {
    key: 'featuresDelivered',
    name: 'Features Delivered',
    description: 'Number of features completed and released',
    formula: 'Count of completed features in time period',
    unit: 'count',
    category: 'delivery',
    interpretation: {
      good: 'Meeting or exceeding delivery targets',
      warning: 'Slightly below delivery targets',
      critical: 'Significantly below delivery targets',
    },
    relatedMetrics: ['featureCost', 'velocity'],
  },
  cycleTime: {
    key: 'cycleTime',
    name: 'Cycle Time',
    description: 'Time from work start to completion',
    formula: 'Average time from "In Progress" to "Done"',
    unit: 'days',
    category: 'delivery',
    interpretation: {
      good: 'Consistent and predictable cycle times',
      warning: 'High variance in cycle times',
      critical: 'Increasing cycle time trend',
    },
    relatedMetrics: ['leadTimeForChanges', 'velocity'],
  },
};

/**
 * Get metric definition by key
 */
export function getMetricDefinition(key: string): MetricDefinition | undefined {
  return METRIC_DEFINITIONS[key];
}

/**
 * Get all metrics for a specific category
 */
export function getMetricsByCategory(
  category: MetricDefinition['category']
): MetricDefinition[] {
  return Object.values(METRIC_DEFINITIONS).filter(
    metric => metric.category === category
  );
}

/**
 * Get related metrics for a given metric
 */
export function getRelatedMetrics(key: string): MetricDefinition[] {
  const metric = getMetricDefinition(key);
  if (!metric || !metric.relatedMetrics) {
    return [];
  }

  return metric.relatedMetrics
    .map(relatedKey => getMetricDefinition(relatedKey))
    .filter((def): def is MetricDefinition => def !== undefined);
}

/**
 * Format metric value with appropriate unit and formatting
 */
export function formatMetricValue(
  value: number,
  definition: MetricDefinition
): string {
  if (definition.unit === 'percentage') {
    return `${value.toFixed(1)}%`;
  }

  if (definition.unit === 'currency') {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(value);
  }

  if (definition.unit?.includes('per')) {
    return `${value.toFixed(2)} ${definition.unit}`;
  }

  if (value >= 1000000) {
    return `${(value / 1000000).toFixed(1)}M ${definition.unit || ''}`.trim();
  }

  if (value >= 1000) {
    return `${(value / 1000).toFixed(1)}K ${definition.unit || ''}`.trim();
  }

  return `${value.toLocaleString()} ${definition.unit || ''}`.trim();
}
